import React, { useEffect, useState } from 'react';
import { X, Mail, Lock, Loader2, Check, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLinkedT } from '../utils/linkedAccountsTranslations';

/**
 * AddLinkedAccountModal — 3-step flow (creds+captcha → OTP → done)
 * that links ONE additional account (of the opposite kind: PRO↔ID) to the
 * currently authenticated user. Uses the existing captcha + OTP infrastructure.
 */
const AddLinkedAccountModal = ({ onClose, onLinked, currentAccountType }) => {
  const lt = useLinkedT();
  const [step, setStep] = useState('creds'); // creds | otp
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [emailMasked, setEmailMasked] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const needKind = currentAccountType === 'PRO' ? 'ID' : 'PRO';

  const loadCaptcha = async () => {
    try {
      const res = await axios.get('/api/auth/captcha');
      setCaptcha(res.data);
      setCaptchaAnswer('');
    } catch (e) {
      // Non-blocking
    }
  };

  useEffect(() => { loadCaptcha(); }, []);

  const submitCreds = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Email et mot de passe requis');
    if (!captchaAnswer.trim()) return toast.error('Veuillez résoudre le calcul');
    setLoading(true);
    try {
      const res = await axios.post('/api/accounts/link/start', {
        email: email.trim(),
        password,
        captcha_answer: captchaAnswer.trim(),
        captcha_token: captcha?.token,
        captcha_question: captcha?.question,
      });
      setTargetUserId(res.data.target_user_id);
      setEmailMasked(res.data.email_masked);
      setStep('otp');
      toast.success(lt('codeSent', { email: res.data.email_masked }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la liaison');
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error('Code requis');
    setLoading(true);
    try {
      const res = await axios.post('/api/accounts/link/verify', {
        target_user_id: targetUserId,
        code: code.trim(),
      });
      toast.success(lt('ok'));
      onLinked?.(res.data.accounts || []);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Code invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto pt-8 pb-4 px-4" data-testid="add-linked-account-modal">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">{lt('modalTitle', { kind: needKind })}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="close-add-linked-account" aria-label={lt('close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'creds' && (
          <form onSubmit={submitCreds} className="space-y-4">
            <p className="text-sm text-gray-400">
              {lt('step1Hint', { kind: needKind })}
            </p>
            <div>
              <label className="text-sm font-medium mb-1 flex items-center gap-2"><Mail className="w-4 h-4" />{lt('email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500"
                autoComplete="off"
                required
                data-testid="linked-account-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 flex items-center gap-2"><Lock className="w-4 h-4" />{lt('password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500"
                autoComplete="off"
                required
                data-testid="linked-account-password"
              />
            </div>
            {captcha && (
              <div>
                <label className="text-sm font-medium mb-1 block">{lt('captchaLabel')} <span className="font-mono">{captcha.question} = ?</span></label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value.replace(/[^0-9-]/g, ''))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500"
                  required
                  data-testid="linked-account-captcha"
                />
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-magenta-500 to-violet-600 rounded-full font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="linked-account-submit-creds"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {lt('sendCode')}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={submitOtp} className="space-y-4">
            <p className="text-sm text-gray-400">
              {lt('otpHint', { email: emailMasked })}
            </p>
            <div>
              <label className="text-sm font-medium mb-1 block">{lt('codeLabel')}</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500 text-center text-2xl tracking-widest font-mono"
                required
                data-testid="linked-account-code"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={loading || code.length < 4}
              className="w-full py-3 bg-gradient-to-r from-magenta-500 to-violet-600 rounded-full font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="linked-account-submit-otp"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {lt('linkBtn')}
            </button>
            <button
              type="button"
              onClick={() => setStep('creds')}
              className="w-full py-2 text-sm text-gray-400 hover:text-white"
            >
              {lt('back')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddLinkedAccountModal;
